import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Mail, MapPin, Phone, ArrowRight } from 'lucide-react';
import { DynamicLogo } from './DynamicLogo';

const footerLinks = {
  shop: [
    { label: 'All Products', href: '/shop' },
    { label: 'Skin Care', href: '/shop?category=skincare' },
    { label: 'Body Care', href: '/shop?category=body-care' },
    { label: 'Herbal Teas', href: '/shop?category=tea' },
    { label: 'Gel Candles', href: '/shop?category=candles' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Our Story', href: '/about#story' },
    { label: 'The Journal', href: '/blog' },
    { label: 'Skin Analysis', href: '/skin-analysis' },
    { label: 'Become an Agent', href: '/member/become-agent' },
  ],
  support: [
    { label: 'Contact Us', href: '/contact' },
    { label: 'FAQs', href: '/faq' },
    { label: 'Shipping & Returns', href: '/shipping-returns' },
  ],
  member: [
    { label: 'Create Account', href: '/member/create-account' },
    { label: 'Sign In', href: '/member/login' },
    { label: 'My Dashboard', href: '/member/dashboard' },
    { label: 'Skin Analysis', href: '/skin-analysis' },
    { label: 'Agent Portal', href: '/agent-portal' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
  ],
};

const socialLinks = [
  { icon: Instagram, href: 'https://instagram.com/marpleskincare', label: 'Instagram' },
  { icon: Facebook, href: 'https://facebook.com/marpleskincare', label: 'Facebook' },
  { icon: Youtube, href: 'https://youtube.com/@marpleskincare', label: 'YouTube' },
];

export function Footer() {
  return (
    <footer className="text-white" style={{ background: '#1A2E12' }}>
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="container-marple py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label text-[#BDD630] mb-4 block">Stay Updated</span>
              <h2 className="font-display font-bold text-3xl lg:text-4xl uppercase tracking-tight mb-4">
                Join the Marple Family
              </h2>
              <p className="font-elegant italic text-xl text-white/70">
                Get exclusive offers, skincare tips, and be the first to know about new products.
              </p>
            </div>
            
            <form className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[#BDD630] transition-colors"
              />
              <button
                type="submit"
                className="group px-8 py-4 bg-[#BDD630] text-[#567D38] font-display font-bold text-sm uppercase tracking-wider hover:bg-[#96c23d] transition-colors flex items-center justify-center gap-2"
              >
                Subscribe
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-marple py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <DynamicLogo variant="white" size="lg" showText />
            
            <p className="mt-6 text-white/70 max-w-sm leading-relaxed">
              Root-to-Radiance African Holistic Skincare. From the inside out — using powerful African botanicals + modern actives.
            </p>
            
            {/* Contact Info */}
            <div className="mt-6 space-y-3">
              <a 
                href="mailto:hello@marplecompany.co.za" 
                className="flex items-center gap-3 text-white/70 hover:text-[#BDD630] transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>hello@marplecompany.co.za</span>
              </a>
              <a 
                href="tel:+27123456789" 
                className="flex items-center gap-3 text-white/70 hover:text-[#BDD630] transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>+27 12 345 6789</span>
              </a>
              <div className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>Johannesburg, South Africa</span>
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h3 className="label text-[#BDD630] mb-6">Shop</h3>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="label text-[#BDD630] mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="label text-[#BDD630] mb-6">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Member Links */}
          <div>
            <h3 className="label text-[#BDD630] mb-6">Member</h3>
            <ul className="space-y-3">
              {footerLinks.member.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="label text-[#BDD630] mb-6">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-marple py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-white/50 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Marple & Co. All rights reserved. 
              <span className="hidden md:inline"> EST. 2011</span>
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-white/20 text-white/70 hover:bg-[#BDD630] hover:border-[#BDD630] hover:text-[#1A2E12] transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Payment Methods */}
            <div className="flex items-center gap-3">
              <span className="text-white/50 text-sm">We accept:</span>
              <div className="flex gap-2">
                {['Visa', 'MC', 'Amex', 'PayPal'].map((method) => (
                  <div 
                    key={method}
                    className="w-10 h-6 bg-white/10 rounded flex items-center justify-center text-[10px] text-white/50"
                  >
                    {method}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Octagon */}
      <div className="absolute bottom-0 right-0 w-64 h-64 opacity-5 pointer-events-none">
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="0.5">
          <path d="M50 5 L85 20 L95 50 L85 80 L50 95 L15 80 L5 50 L15 20 Z" />
        </svg>
      </div>
    </footer>
  );
}

export default Footer;
